import React, {
	useCallback,
	useEffect,
	useMemo,
	useState
} from 'react';
import {
	Button,
	Divider,
	Segment
} from 'semantic-ui-react';

import {
	deactivateStaff,
	getAgencyStaffList,
	getAgencyStaff
} from '../../../api/nora/agencies';

import {
	useAgencyState,
	useAgencyAction
} from '../../../contexts/AgenciesContext';
import { useAuthState } from '../../../contexts/AuthContext';
import { useUIAction } from '../../../contexts/UIContext';

import Alert from '../../Alert';
import FirstAndLastName from '../../Table/Cells/FirstAndLastName';
import HelpModal from '../../Shared/HelpModal';
import multiWordFilterFn from '../../Table/filterFunctions/multiWordFilterFn';
import PermissionsWrapper from '../../PermissionsWrapper';
import StaffMobileColumn from './StaffMobileColumn';
import StaffModal from './StaffModal';
import Table from '../../Table';
import TableRowActions from '../../Table/Cells/TableRowActions';

import styles from '../agency-portal.module.css';

const Staff = () => {
	const { agency } = useAgencyState();
	const { setAgencyStaff } = useAgencyAction();
	const { user: { permissions } } = useAuthState();
	const {
		setApiErrorAlert,
		setInfobarLoader
	} = useUIAction();

	const [ employee, setEmployee ] = useState( null );
	const [ deactivateConfirm, setDeactivateConfirm ] = useState( false );
	const [ staffToDeactivate, setStaffToDeactivate ] = useState( {} );
	const [ width, setWidth ] = useState( window.innerWidth );

	useEffect( () => {
		const handleResize = () => setWidth( window.innerWidth );

		window.addEventListener( 'resize', handleResize );

		return () => window.removeEventListener( 'resize', handleResize );
	}, [] );

	const retrieveAgencyStaff = useCallback( async () => {
		setInfobarLoader( 'Retrieving Staff List...' );

		try {
			const data = await getAgencyStaffList( agency.id );

			setAgencyStaff( data );
		} catch ( error ) {
			setApiErrorAlert( error );
		}

		setInfobarLoader( '' );
	}, [
		agency.id,
		setApiErrorAlert,
		setInfobarLoader,
		setAgencyStaff
	] );

	// useEffect( () => {
	// 	( async () => {
	// 		if ( permissions.agencyListStaff.canExecute && permissions.agencyListStaff.isAvailable ) {
	// 			await retrieveAgencyStaff();
	// 		} else {
	// 			setInfobarLoader( '' );
	// 		}
	// 	} )();
	// }, [
	// 	permissions,
	// 	retrieveAgencyStaff,
	// 	setInfobarLoader
	// ] );

	const handleCloseStaffModal = useCallback( () => {
		setEmployee( null );
	}, [] );

	const handleAddStaff = useCallback( () => {
		setEmployee( {} );
	}, [] );

	const handleDeactivateStaff = useCallback( async ( id, statusID ) => {
		setInfobarLoader( 'Retrieving Location List...' );

		try {
			if ( statusID !== 5 ) {
				await deactivateStaff( agency.id, id );
			}

			await retrieveAgencyStaff();
		} catch ( error ) {
			setApiErrorAlert( error );
		}

		setInfobarLoader( '' );
	}, [
		setInfobarLoader,
		retrieveAgencyStaff,
		agency.id,
		setApiErrorAlert
	] );

	const deactivateConfirmMessage = useMemo( () => <span>Are you sure you would like to deactivate <strong>{ `${ staffToDeactivate.firstName } ${ staffToDeactivate.lastName }` }</strong>?</span>, [ staffToDeactivate ] );

	const deactivateConfirmCloseModal = useCallback( () => setDeactivateConfirm( false ), [] );

	// Data Table

	const firstAndLastNameCell = useCallback( ( { row } ) => <FirstAndLastName row={ row.original } />, [] );

	const staffMobileColumn = useCallback( ( { row } ) => <StaffMobileColumn row={row.original} />, [] );

	const onEdit = useCallback( async ( { id } ) => {
		const employee = await getAgencyStaff( agency.id, id );

		setEmployee( employee );
	}, [ agency.id ] );

	const onDeactivate = useCallback( staff => {
		setStaffToDeactivate( staff );
		setDeactivateConfirm( true );
	}, [] );

	const tableRowActionsCell = useCallback( ( { row } ) => {
		const actions = [
			{
				action: onEdit,
				componentProps: { color: 'pink' },
				icon: 'search',
				permission: permissions.agencyEditStaff.canExecute,
				text: 'Edit Location'
			},
			{
				action: onDeactivate,
				componentProps: { color: 'red' },
				icon: 'remove user',
				permission: permissions.agencyEditStaff.canExecute,
				text: 'Deactivate Staff'
			}
		];

		return (
			<TableRowActions
				actions={ actions }
				row={ row.original }
			/>
		);
	}, [
		onDeactivate,
		onEdit,
		permissions.agencyEditStaff.canExecute
	] );

	const columns = useMemo( () => [
		{
			id: 'name',
			accessor: row => `${ row.firstName }${ row.lastName }`,
			Header: 'Name',
			filter: multiWordFilterFn,
			Cell: firstAndLastNameCell,
			minWidth: 60
		},
		{
			accessor: 'title',
			Header: 'Title',
			filter: multiWordFilterFn,
			minWidth: 60
		},
		{
			accessor: 'email',
			Header: 'Email',
			filter: multiWordFilterFn,
			minWidth: 60
		}
	], [ firstAndLastNameCell ] );

	const mobileColumns = useMemo( () => [
		{
			id: 'mobile_column',
			accessor: row => `${ row.firstName }${ row.lastName }${ row.title }${ row.email }`,
			Header: 'Staff Members',
			filter: multiWordFilterFn,
			Cell: staffMobileColumn,
			minWidth: 60
		}

	], [ staffMobileColumn ] );

	const actionColumn = useMemo( () => ( {
		id: 'id_actions',
		Header: 'Edit / Deactivate',
		Cell: tableRowActionsCell,
		disableFilters: true,
		disableResizing: true,
		width: 100,
		minWidth: 100,
		maxWidth: 100
	} ), [ tableRowActionsCell ] );

	const renderColumns = useMemo(
		() => permissions.agencyEditAgency.isAvailable
			? [
				...width < 767 ? mobileColumns : columns,
				actionColumn
			]
			: width < 767 ? mobileColumns : columns,
		[
			actionColumn,
			columns,
			mobileColumns,
			permissions.agencyEditAgency.isAvailable,
			width
		]
	);

	return (
		<>
			<div className={ styles.container } >
				<header className={ styles.pageHeader }>
					<h2>
						Staff
						<HelpModal
							id={ 7 }
							triggerStyles={ { verticalAlign: 'top' } }
						/>
					</h2>
					<PermissionsWrapper permission={ permissions.agencyAddStaff.isAvailable }>
						<Button
							className={ styles.newStaffButton }
							disabled={ !permissions.agencyAddStaff.canExecute }
							inverted
							icon="add"
							onClick={ handleAddStaff }
							size="tiny"
						/>
					</PermissionsWrapper>
				</header>
				<Segment
					basic as="div"
					className={ styles.content }
				>
					<PermissionsWrapper
						alt={ <p>Access denied.</p> }
						permission={ permissions.agencyListStaff.canExecute }
					>
						<Table
							columns={ renderColumns }
							data={ agency.staff }
							showCount={ agency.staff.length > 10 }
							showEmptyRows={ false }
							showPagination={ agency.staff.length > 10 }
							style={ { overflowX: 'auto' } }
						/>
					</PermissionsWrapper>
				</Segment>
			</div>
			<Divider hidden />
			<PermissionsWrapper
				permission={ permissions.agencyAddStaff.isAvailable && permissions.agencyAddStaff.canExecute }
			>
				<StaffModal
					open={ Boolean( employee ) }
					closeModal={ handleCloseStaffModal }
					employee={ employee }
					onSave={ retrieveAgencyStaff }
				/>
			</PermissionsWrapper>

			<Alert
				action={ handleDeactivateStaff }
				name="unassign-coordination-confirm-alert"
				message={ deactivateConfirmMessage }
				open={ deactivateConfirm }
				closeModal={ deactivateConfirmCloseModal }
				type="delete"
			/>
		</>
	);
};

export default Staff;
